/**
 * MatchmakingModal.tsx
 * Main matchmaking orchestrator component
 */

import {
  type FC,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ELeague,
  ERematchRequestStatus,
  EFreePlayDifficulty,
  GameReturnDto,
  getSafeLeagueForGame,
  getStakeCapCentsForLeague,
  getRatingForGame,
  getWinStreakRequiredForNextLeague,
  type IBaseSingleResultDto,
  type IGameAction,
  type RematchRequestResponseDto,
  type RequestRematchDto,
  type DeclineRematchDto,
} from '@skillarea/lib-shared';
import { Modal, toast } from '@skillarea/lib-frontend';
import { useTranslation } from 'react-i18next';

import { preloadPhaserGame } from '../games/preload-phaser-game';

import styles from './MatchmakingModal.module.scss';
import {
  useMatchmakingSocket,
  type TMatchmakingState,
} from './hooks/useMatchmakingSocket';
import {
  StakeSelectionScreen,
  type IGameMetadata,
} from './screens/StakeSelectionScreen';
import { SearchingScreen } from './screens/SearchingScreen';
import { StakeOfferScreen } from './screens/StakeOfferScreen';
import { StakeOfferDeclinedScreen } from './screens/StakeOfferDeclinedScreen';
import { GameConfirmScreen } from './screens/GameConfirmScreen';
import { GameScreen } from './screens/GameScreen';
import { WinScreen } from './screens/WinScreen';
import { LoseScreen } from './screens/LoseScreen';
import { DrawScreen } from './screens/DrawScreen';
import { CancelledScreen } from './screens/CancelledScreen';
import { ErrorScreen } from './screens/ErrorScreen';
import { ActiveGameExistsScreen } from './screens/ActiveGameExistsScreen';
import { RematchWaitingScreen } from './screens/RematchWaitingScreen';

import { useLocalizedGame } from '~/hooks/useLocalizedGame';
import {
  incrementFreePlayFinishedGamesToday,
  incrementNonFreePlayFinishedGamesToday,
  markChestUnlockedModalPendingToday,
  markWinMoreModalPendingToday,
  markWinRealMoneyModalPendingToday,
  WIN_REAL_MONEY_MODAL_FREE_PLAY_GAMES_THRESHOLD,
  WIN_REAL_MONEY_MODAL_MAX_BALANCE_CENTS,
} from '~/lib/one-time-modal-storage';
import { useUserGameStats, useUserId, useUserStore } from '~/stores/userStore';
import { getConfigs, type IRequestOptions } from '~/services/options';
import { serviceOptions } from '~/services';
import { pushGa4Event } from '~/lib/analytics/gtm';
import { hideTawkWidget, showTawkWidget } from '~/lib/tawk';
import {
  playLoseSound,
  playOpponentFoundSound,
  playStakeWinSound,
  playWinSound,
} from '~/lib/game-sounds';
import { gameQueries } from '~/stores/gameStore';
import {
  isMatchmakingInsufficientBalanceError,
  translateMatchmakingError,
  translateMatchmakingSearchMessage,
} from '~/lib/matchmakingLocale';

const CALIBRATION_GAMES_THRESHOLD = 5;

const CLOSEABLE_STATES: TMatchmakingState[] = [
  'win',
  'lose',
  'draw',
  'cancelled',
  'error',
  'idle',
  'connecting',
  'stake_selection',
];

type TResultScreenState = 'win' | 'lose' | 'draw';
type TLocalMatchmakingState = TMatchmakingState | 'rematch_waiting';

interface IRematchWaitingState {
  previousResultState: TResultScreenState;
  rematchRequestId: string;
  expiresAtMs: number;
  isAccepted: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isBaseSingleResultDto(
  value: unknown,
): value is IBaseSingleResultDto<unknown> {
  if (!isRecord(value)) return false;
  return (
    typeof value.success === 'boolean' &&
    typeof value.status === 'number' &&
    'data' in value
  );
}

function hasBaseResultFields(value: unknown): value is {
  success: boolean;
  status: number;
  error?: unknown;
  dtoMessages?: unknown;
} {
  if (!isRecord(value)) return false;
  return typeof value.success === 'boolean' && typeof value.status === 'number';
}

function toBaseSingleResultDto<T>(payload: unknown): IBaseSingleResultDto<T> {
  if (isBaseSingleResultDto(payload)) {
    return payload as IBaseSingleResultDto<T>;
  }

  if (!hasBaseResultFields(payload)) {
    throw new Error('Unexpected API payload shape');
  }

  const { dtoMessages, error, status, success, ...dataWithoutMeta } = payload;

  const hasOwnData = Object.prototype.hasOwnProperty.call(payload, 'data');
  const data = hasOwnData
    ? (payload as Record<string, unknown>).data
    : dataWithoutMeta;

  return {
    success,
    status,
    error: typeof error === 'string' ? error : undefined,
    dtoMessages: Array.isArray(dtoMessages)
      ? (dtoMessages as IBaseSingleResultDto<T>['dtoMessages'])
      : undefined,
    data: data as T,
  };
}

function getExpiresAtMs(expiresAt: unknown): number {
  if (expiresAt instanceof Date) {
    return expiresAt.getTime();
  }
  if (typeof expiresAt === 'string') {
    const ms = Date.parse(expiresAt);
    if (!Number.isNaN(ms)) return ms;
  }
  if (typeof expiresAt === 'number' && Number.isFinite(expiresAt)) {
    return expiresAt;
  }
  throw new Error('Invalid expiresAt value');
}

async function requestApi<T>(opts: {
  method: string;
  url: string;
  body?: unknown;
  options?: IRequestOptions;
}): Promise<IBaseSingleResultDto<T>> {
  const configs = getConfigs(
    opts.method,
    'application/json',
    opts.url,
    opts.options ?? {},
  );
  if (opts.body !== undefined) {
    configs.data = opts.body;
  }

  const requester: unknown = serviceOptions.axios?.request;
  if (typeof requester !== 'function') {
    throw new Error('API requester is not configured (serviceOptions.axios)');
  }

  const response = (await (requester as (cfg: unknown) => Promise<unknown>)(
    configs,
  )) as unknown;

  if (!isRecord(response) || !('data' in response)) {
    throw new Error('Unexpected API response shape');
  }

  const data = (response as Record<string, unknown>).data;
  return toBaseSingleResultDto<T>(data);
}

export interface IMatchmakingModalProps {
  game: GameReturnDto;
  gameMetadata?: IGameMetadata;
  initialStake?: number;
  onClose: () => void;
  userBalance: number;
}

export const MatchmakingModal: FC<IMatchmakingModalProps> = ({
  game,
  gameMetadata,
  initialStake,
  onClose,
  userBalance,
}) => {
  const { t } = useTranslation('matchmaking');
  const localizedGame = useLocalizedGame(game);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const matchmaking = useMatchmakingSocket();
  const { state: matchmakingState, syncStateForModalOpen } = matchmaking;

  const gameStats = useUserGameStats();
  const userId = useUserId();
  const loadUser = useUserStore((s) => s.loadUser);
  const effectiveLeague = useMemo(() => {
    const minLeague =
      gameStats?.minLeaguePerGame?.[String(game.id)] ?? ELeague.IRON;
    const gamesPlayedRaw = gameStats?.gamesPerGame?.[game.id];
    const gamesPlayed =
      typeof gamesPlayedRaw === 'number' && Number.isFinite(gamesPlayedRaw)
        ? gamesPlayedRaw
        : 0;

    if (
      minLeague === ELeague.IRON &&
      gamesPlayed < CALIBRATION_GAMES_THRESHOLD
    ) {
      return ELeague.IRON;
    }

    return getSafeLeagueForGame(gameStats, game.id, 1000);
  }, [game.id, gameStats]);
  const stakeCapCents = useMemo(
    () => getStakeCapCentsForLeague(effectiveLeague),
    [effectiveLeague],
  );

  const nextLeagueWins = useMemo(() => {
    const rating = getRatingForGame(gameStats, game.id, 1000);
    return getWinStreakRequiredForNextLeague(rating);
  }, [gameStats, game.id]);

  const isFreePlayMatchResult = useMemo(
    () => matchmaking.sessionIsFreePlay || matchmaking.gameInfo?.stake === 0,
    [matchmaking.sessionIsFreePlay, matchmaking.gameInfo?.stake],
  );

  const [localState, setLocalState] = useState<TLocalMatchmakingState>(() => {
    const managerState = matchmakingState;
    return managerState === 'idle' ? 'connecting' : managerState;
  });
  const canClose =
    localState !== 'rematch_waiting' && CLOSEABLE_STATES.includes(localState);

  const [rematchWaiting, setRematchWaiting] =
    useState<IRematchWaitingState | null>(null);
  const [rematchTimeLeftMs, setRematchTimeLeftMs] = useState<number | null>(
    null,
  );
  const [confirmTimeLeftMs, setConfirmTimeLeftMs] = useState<number>(0);
  const [stakeSelectionInitialStake, setStakeSelectionInitialStake] = useState<
    number | undefined
  >(initialStake);
  const [insufficientBalanceModalNonce, setInsufficientBalanceModalNonce] =
    useState(0);
  const [isRematchRequesting, setIsRematchRequesting] = useState(false);
  const [isRematchDeclining, setIsRematchDeclining] = useState(false);
  const [isCancellingSearch, setIsCancellingSearch] = useState(false);
  const rematchTimeoutHandledForRequestIdRef = useRef<string | null>(null);
  const rematchCountdownIntervalIdRef = useRef<number | null>(null);
  const confirmCountdownIntervalIdRef = useRef<number | null>(null);
  const initialGameStateRequestedLobbyIdRef = useRef<string | null>(null);
  const readySentForLobbyIdRef = useRef<string | null>(null);
  const completedNonFreePlayLobbyIdRef = useRef<string | null>(null);
  const completedFreePlayLobbyIdRef = useRef<string | null>(null);
  const chestUnlockMarkedForLobbyIdRef = useRef<string | null>(null);

  const clearRematchCountdownInterval = useCallback(() => {
    if (rematchCountdownIntervalIdRef.current !== null) {
      window.clearInterval(rematchCountdownIntervalIdRef.current);
      rematchCountdownIntervalIdRef.current = null;
    }
  }, []);

  const clearConfirmCountdownInterval = useCallback(() => {
    if (confirmCountdownIntervalIdRef.current !== null) {
      window.clearInterval(confirmCountdownIntervalIdRef.current);
      confirmCountdownIntervalIdRef.current = null;
    }
  }, []);

  const clearRematchCountdownState = useCallback(
    (nextState?: TResultScreenState) => {
      clearRematchCountdownInterval();
      setRematchWaiting(null);
      setRematchTimeLeftMs(null);
      if (nextState) {
        setLocalState(nextState);
      }
    },
    [clearRematchCountdownInterval],
  );

  const resolvePreviousResultState = useCallback(
    (
      currentState: TLocalMatchmakingState,
      currentRematchWaiting: IRematchWaitingState | null,
    ): TResultScreenState | null => {
      if (
        currentState === 'win' ||
        currentState === 'lose' ||
        currentState === 'draw'
      ) {
        return currentState;
      }

      if (currentRematchWaiting) {
        return currentRematchWaiting.previousResultState;
      }

      return null;
    },
    [],
  );

  const seedRematchCountdownState = useCallback(
    (params: {
      previousResultState: TResultScreenState;
      rematchRequestId: string;
      expiresAt: unknown;
      isAccepted: boolean;
    }) => {
      const expiresAtMs = getExpiresAtMs(params.expiresAt);
      const msLeft = Math.max(0, expiresAtMs - Date.now());

      rematchTimeoutHandledForRequestIdRef.current = null;
      clearRematchCountdownInterval();
      setRematchWaiting({
        previousResultState: params.previousResultState,
        rematchRequestId: params.rematchRequestId,
        expiresAtMs,
        isAccepted: params.isAccepted,
      });
      setRematchTimeLeftMs(msLeft);
      setLocalState('rematch_waiting');
    },
    [clearRematchCountdownInterval],
  );

  useEffect(() => {
    hideTawkWidget();

    return () => {
      showTawkWidget();
    };
  }, []);

  useEffect(() => {
    setStakeSelectionInitialStake(initialStake);
  }, [initialStake]);

  useEffect(() => {
    if (
      localState === 'stake_selection' &&
      isMatchmakingInsufficientBalanceError(matchmaking.error)
    ) {
      setInsufficientBalanceModalNonce((current) => current + 1);
    }
  }, [localState, matchmaking.error]);

  useEffect(() => {
    if (localState === 'playing') {
      clearRematchCountdownState();
      rematchTimeoutHandledForRequestIdRef.current = null;
    }
  }, [clearRematchCountdownState, localState]);

  useEffect(() => {
    const lobbyId = matchmaking.gameInfo?.lobbyId ?? matchmaking.currentLobbyId;

    if (localState !== 'preparing') {
      readySentForLobbyIdRef.current = null;
      return;
    }

    if (!lobbyId) {
      return;
    }

    if (readySentForLobbyIdRef.current === lobbyId) {
      return;
    }

    readySentForLobbyIdRef.current = lobbyId;

    preloadPhaserGame(game.slug)
      .catch(() => undefined)
      .finally(() => {
        matchmaking.clientReady();
      });
  }, [
    game.slug,
    localState,
    matchmaking,
    matchmaking.currentLobbyId,
    matchmaking.gameInfo?.lobbyId,
  ]);

  useEffect(() => {
    return () => {
      clearRematchCountdownInterval();
      clearConfirmCountdownInterval();
    };
  }, [clearConfirmCountdownInterval, clearRematchCountdownInterval]);

  useEffect(() => {
    clearConfirmCountdownInterval();

    if (
      localState !== 'game_confirm' &&
      localState !== 'waiting_for_opponent'
    ) {
      setConfirmTimeLeftMs(0);
      return;
    }

    const confirmDeadlineAtMs = matchmaking.gameInfo?.confirmDeadlineAtMs;
    if (
      typeof confirmDeadlineAtMs !== 'number' ||
      !Number.isFinite(confirmDeadlineAtMs)
    ) {
      setConfirmTimeLeftMs(0);
      return;
    }

    const tick = () => {
      setConfirmTimeLeftMs(Math.max(0, confirmDeadlineAtMs - Date.now()));
    };

    tick();
    confirmCountdownIntervalIdRef.current = window.setInterval(tick, 1000);

    return () => {
      clearConfirmCountdownInterval();
    };
  }, [
    clearConfirmCountdownInterval,
    localState,
    matchmaking.gameInfo?.confirmDeadlineAtMs,
  ]);

  useEffect(() => {
    const currentLobbyId = matchmaking.gameInfo?.lobbyId ?? null;

    if (localState !== 'playing') {
      initialGameStateRequestedLobbyIdRef.current = null;
      return;
    }

    if (!currentLobbyId) {
      return;
    }

    if (
      matchmaking.gameUpdate?.player1TimeLeftMs !== undefined &&
      matchmaking.gameUpdate?.player2TimeLeftMs !== undefined
    ) {
      initialGameStateRequestedLobbyIdRef.current = currentLobbyId;
      return;
    }

    if (initialGameStateRequestedLobbyIdRef.current === currentLobbyId) {
      return;
    }

    initialGameStateRequestedLobbyIdRef.current = currentLobbyId;
    matchmaking.requestGameState();
  }, [
    localState,
    matchmaking,
    matchmaking.gameInfo?.lobbyId,
    matchmaking.gameUpdate?.player1TimeLeftMs,
    matchmaking.gameUpdate?.player2TimeLeftMs,
  ]);

  useEffect(() => {
    const unsub = matchmaking.addGameResultImmediateListener((result) => {
      if (result === 'win') {
        if (isFreePlayMatchResult) {
          playWinSound();
        } else {
          void playStakeWinSound();
        }
      }
      if (result === 'lose') playLoseSound();
    });
    return unsub;
  }, [isFreePlayMatchResult, matchmaking]);

  const searchInitialElapsedTimeSeconds = useMemo(() => {
    const searchStartedAtMs = matchmaking.searchState?.searchStartedAtMs;
    if (
      typeof searchStartedAtMs !== 'number' ||
      !Number.isFinite(searchStartedAtMs)
    ) {
      return 0;
    }

    return Math.max(0, Math.floor((Date.now() - searchStartedAtMs) / 1000));
  }, [matchmaking.searchState?.searchStartedAtMs]);

  // State synchronization
  useEffect(() => {
    const unsub = matchmaking.addStateListener((state) => {
      setLocalState(state);
      if (state !== 'searching') {
        setIsCancellingSearch(false);
      }
    });
    return unsub;
  }, [matchmaking]);

  useEffect(() => {
    syncStateForModalOpen(game.id);
  }, [game.id, syncStateForModalOpen]);

  useEffect(() => {
    if (localState !== 'game_confirm') return;
    if (!matchmaking.gameInfo?.lobbyId) return;
    playOpponentFoundSound();
  }, [localState, matchmaking.gameInfo?.lobbyId]);

  useEffect(() => {
    if (
      localState === 'win' ||
      localState === 'lose' ||
      localState === 'draw'
    ) {
      void loadUser();
    }
  }, [loadUser, localState]);

  const emittedResultLobbyIdRef = useRef<string | null>(null);
  const winMorePendingLobbyIdRef = useRef<string | null>(null);
  const winRealMoneyPendingLobbyIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (localState === 'win' || localState === 'lose') {
      const lobbyId = matchmaking.gameResult?.lobbyId;
      if (lobbyId && emittedResultLobbyIdRef.current !== lobbyId) {
        emittedResultLobbyIdRef.current = lobbyId;
        pushGa4Event(localState === 'lose' ? 'game_lost' : 'game_won', {
          items: [{ item_name: localizedGame.name }],
          value: matchmaking.gameInfo?.stake ?? 0,
          currency: 'USD',
          match_outcome: localState === 'lose' ? 'loss' : 'win',
        });
      }
    }
  }, [
    localState,
    localizedGame.name,
    matchmaking.gameResult?.lobbyId,
    matchmaking.gameInfo?.stake,
  ]);

  useEffect(() => {
    if (
      localState !== 'win' &&
      localState !== 'lose' &&
      localState !== 'draw'
    ) {
      return;
    }

    if (isFreePlayMatchResult) {
      return;
    }

    const lobbyId = matchmaking.gameResult?.lobbyId;
    if (!lobbyId || completedNonFreePlayLobbyIdRef.current === lobbyId) {
      return;
    }

    if (!userId) {
      return;
    }

    completedNonFreePlayLobbyIdRef.current = lobbyId;
    const count = incrementNonFreePlayFinishedGamesToday(userId);
    if (count === 2 && winMorePendingLobbyIdRef.current !== lobbyId) {
      winMorePendingLobbyIdRef.current = lobbyId;
      markWinMoreModalPendingToday(userId);
    }
  }, [
    isFreePlayMatchResult,
    localState,
    matchmaking.gameResult?.lobbyId,
    userId,
  ]);

  useEffect(() => {
    if (
      localState !== 'win' &&
      localState !== 'lose' &&
      localState !== 'draw'
    ) {
      return;
    }

    if (!isFreePlayMatchResult) {
      return;
    }

    const lobbyId = matchmaking.gameResult?.lobbyId;
    if (!lobbyId || completedFreePlayLobbyIdRef.current === lobbyId) {
      return;
    }

    if (!userId) {
      return;
    }

    if (userBalance >= WIN_REAL_MONEY_MODAL_MAX_BALANCE_CENTS) {
      return;
    }

    completedFreePlayLobbyIdRef.current = lobbyId;
    const count = incrementFreePlayFinishedGamesToday(userId);
    if (
      count === WIN_REAL_MONEY_MODAL_FREE_PLAY_GAMES_THRESHOLD &&
      winRealMoneyPendingLobbyIdRef.current !== lobbyId
    ) {
      winRealMoneyPendingLobbyIdRef.current = lobbyId;
      markWinRealMoneyModalPendingToday(userId);
    }
  }, [
    isFreePlayMatchResult,
    localState,
    matchmaking.gameResult?.lobbyId,
    userBalance,
    userId,
  ]);

  useEffect(() => {
    const lobbyId = matchmaking.gameResult?.lobbyId;
    if (!lobbyId) return;
    if (chestUnlockMarkedForLobbyIdRef.current === lobbyId) return;
    if (!userId) return;

    if (matchmaking.gameResult?.chestUnlockedDayIndex == null) {
      return;
    }

    chestUnlockMarkedForLobbyIdRef.current = lobbyId;
    markChestUnlockedModalPendingToday(userId);
    void queryClient.invalidateQueries({
      queryKey: ['daily-rewards', 'state'],
    });
  }, [
    matchmaking.gameResult?.chestUnlockedDayIndex,
    matchmaking.gameResult?.lobbyId,
    queryClient,
    userId,
  ]);

  const handleRematchTimeout = useCallback(
    (state: IRematchWaitingState) => {
      if (
        rematchTimeoutHandledForRequestIdRef.current === state.rematchRequestId
      ) {
        return;
      }
      rematchTimeoutHandledForRequestIdRef.current = state.rematchRequestId;

      clearRematchCountdownState(state.previousResultState);
      toast?.warning(t('error.rematchTimedOut'), {
        dedupeKey: `rematch-timeout-${state.rematchRequestId}`,
      });
    },
    [clearRematchCountdownState, t],
  );

  useEffect(() => {
    clearRematchCountdownInterval();

    if (!rematchWaiting) return;
    if (localState !== 'rematch_waiting') return;
    if (rematchWaiting.isAccepted) return;

    const tick = () => {
      const msLeft = rematchWaiting.expiresAtMs - Date.now();
      const safeLeft = Number.isFinite(msLeft) ? Math.max(0, msLeft) : 0;
      setRematchTimeLeftMs(safeLeft);
      if (safeLeft === 0) {
        handleRematchTimeout(rematchWaiting);
      }
    };

    tick();
    rematchCountdownIntervalIdRef.current = window.setInterval(tick, 1000);

    return () => {
      clearRematchCountdownInterval();
    };
  }, [
    clearRematchCountdownInterval,
    handleRematchTimeout,
    localState,
    rematchWaiting,
  ]);

  useEffect(() => {
    const unsub = matchmaking.addRematchStateListener((update) => {
      const previousResultState = resolvePreviousResultState(
        localState,
        rematchWaiting,
      );

      if (
        update.transition === 'request' &&
        (update.status === ERematchRequestStatus.pending ||
          update.status === ERematchRequestStatus.accepted) &&
        previousResultState !== null
      ) {
        seedRematchCountdownState({
          previousResultState,
          rematchRequestId: update.rematchRequestId,
          expiresAt: update.expiresAt,
          isAccepted: update.status === ERematchRequestStatus.accepted,
        });
        return;
      }

      if (!rematchWaiting) return;
      if (update.rematchRequestId !== rematchWaiting.rematchRequestId) return;

      if (update.status === ERematchRequestStatus.declined) {
        rematchTimeoutHandledForRequestIdRef.current =
          rematchWaiting.rematchRequestId;
        clearRematchCountdownState(rematchWaiting.previousResultState);
        toast?.warning(t('error.rematchDeclined'), {
          dedupeKey: `rematch-declined-${rematchWaiting.rematchRequestId}`,
        });
        return;
      }

      if (update.status === ERematchRequestStatus.expired) {
        handleRematchTimeout(rematchWaiting);
        return;
      }

      if (update.status === ERematchRequestStatus.accepted) {
        clearRematchCountdownInterval();
        setRematchWaiting((current) => {
          if (!current) return current;
          if (current.rematchRequestId !== update.rematchRequestId) {
            return current;
          }

          return {
            ...current,
            isAccepted: true,
          };
        });
      }
    });

    return unsub;
  }, [
    clearRematchCountdownInterval,
    clearRematchCountdownState,
    handleRematchTimeout,
    localState,
    matchmaking,
    rematchWaiting,
    resolvePreviousResultState,
    seedRematchCountdownState,
    t,
  ]);

  // Event handlers
  const handleCancelSearch = useCallback(() => {
    if (isCancellingSearch) return;
    setIsCancellingSearch(true);

    const emitted = matchmaking.cancelSearch();
    if (!emitted) {
      // Fallback if socket was not connected
      requestApi<void>({
        method: 'post',
        url: '/api/matchmaking/cancel-search',
      })
        .then((result) => {
          if (!result.success) {
            toast?.error(
              translateMatchmakingError(
                t,
                result.error ?? 'Failed to cancel search',
              ),
            );
            setIsCancellingSearch(false);
          } else {
            // Success via REST, let's update localState since socket won't
            matchmaking.reset();
            setLocalState('cancelled');
            setIsCancellingSearch(false);
          }
        })
        .catch((err) => {
          console.error('Cancel search REST fallback failed', err);
          toast?.error(t('error.cancelSearchFailed'));
          setIsCancellingSearch(false);
        });
    }
  }, [isCancellingSearch, matchmaking, t]);

  const handleFindOpponent = useCallback(
    (stake: number) => {
      setStakeSelectionInitialStake(stake);
      matchmaking.findGame(game.id, stake);
    },
    [game.id, matchmaking],
  );

  const handleFreePlay = useCallback(
    (difficulty: EFreePlayDifficulty) => {
      // Free play must not affect the daily rewards baseline.
      setStakeSelectionInitialStake(undefined);
      matchmaking.freePlay(game.id, difficulty);
    },
    [game.id, matchmaking],
  );

  const handleAcceptStakeOffer = useCallback(() => {
    const stakeOffer = matchmaking.stakeOffer;
    if (stakeOffer) {
      matchmaking.acceptStakeOffer(stakeOffer.suggestedStake);
    }
  }, [matchmaking]);

  const handleDeclineStakeOffer = useCallback(() => {
    matchmaking.declineStakeOffer();
  }, [matchmaking]);

  const handleContinueFromDeclined = useCallback(() => {
    setLocalState('searching');
  }, []);

  const handleRetry = useCallback(() => {
    syncStateForModalOpen(game.id);
  }, [game.id, syncStateForModalOpen]);

  const handleAcceptGame = useCallback(() => {
    matchmaking.confirmGame();
  }, [matchmaking]);

  const handleDeclineGame = useCallback(() => {
    matchmaking.declineGame();
    setStakeSelectionInitialStake(matchmaking.gameInfo?.stake);
    setLocalState('stake_selection');
  }, [matchmaking, matchmaking.gameInfo?.stake]);

  const handleFindNewOpponent = useCallback(() => {
    const previousStake =
      matchmaking.gameInfo?.stake ?? matchmaking.rematchStateUpdate?.stake;

    clearRematchCountdownState();
    rematchTimeoutHandledForRequestIdRef.current = null;
    matchmaking.reset();
    matchmaking.clearActiveGameId();
    setStakeSelectionInitialStake(previousStake);
    setLocalState('stake_selection');
  }, [
    clearRematchCountdownState,
    matchmaking,
    matchmaking.gameInfo?.stake,
    matchmaking.rematchStateUpdate?.stake,
  ]);

  const handleGoToActiveGame = useCallback(() => {
    const activeGameId = matchmaking.activeGameConflict?.activeGameId;

    if (typeof activeGameId !== 'number') {
      toast?.error(t('error.activeGameIdMissing'));
      return;
    }

    void queryClient
      .ensureQueryData(gameQueries.list({}, queryClient))
      .then((data) => {
        const games = data?.items ? Object.values(data.items) : [];
        const activeGame = games.find(
          (candidateGame) => candidateGame.id === activeGameId,
        );

        if (!activeGame) {
          toast?.error(t('error.activeGameSlugMissing'));
          return;
        }

        matchmaking.reset();
        matchmaking.clearActiveGameId();

        onClose();
        void navigate({
          to: '/games/$gameId',
          params: { gameId: activeGame.slug },
          hash: 'matchmaking',
        });
      });
  }, [
    queryClient,
    matchmaking,
    matchmaking.activeGameConflict?.activeGameId,
    navigate,
    onClose,
    t,
  ]);

  const handleBackFromActiveGameExists = useCallback(() => {
    setLocalState('stake_selection');
  }, []);

  const handleRematch = useCallback(() => {
    const previousResultState = localState;
    const isResultState =
      previousResultState === 'win' ||
      previousResultState === 'lose' ||
      previousResultState === 'draw';
    if (!isResultState) {
      return;
    }

    if (isRematchRequesting) {
      return;
    }

    const sourceLobbyId =
      matchmaking.gameResult?.lobbyId ?? matchmaking.gameInfo?.lobbyId;
    if (!sourceLobbyId) {
      toast?.error(t('error.rematchUnavailable'));
      return;
    }

    setIsRematchRequesting(true);
    clearRematchCountdownState();
    rematchTimeoutHandledForRequestIdRef.current = null;

    (async () => {
      try {
        const body: RequestRematchDto = {
          sourceLobbyId,
        };

        const result = await requestApi<RematchRequestResponseDto>({
          method: 'post',
          url: '/api/matchmaking/rematch/request',
          body,
        });

        if (!result.success) {
          toast?.error(
            translateMatchmakingError(
              t,
              result.error ?? 'Failed to request rematch',
            ),
          );
          return;
        }

        if (result.data.status === ERematchRequestStatus.declined) {
          setRematchWaiting(null);
          setRematchTimeLeftMs(null);
          setLocalState(previousResultState);
          toast?.warning(t('error.rematchDeclined'));
          return;
        }

        if (result.data.status === ERematchRequestStatus.expired) {
          setRematchWaiting(null);
          setRematchTimeLeftMs(null);
          setLocalState(previousResultState);
          toast?.warning(t('error.rematchUnavailableExpired'));
          return;
        }

        seedRematchCountdownState({
          previousResultState,
          rematchRequestId: result.data.rematchRequestId,
          expiresAt: result.data.expiresAt,
          isAccepted: result.data.status === ERematchRequestStatus.accepted,
        });
      } catch (err) {
        console.error('Rematch request failed', err);
        toast?.error(t('error.rematchRequestFailed'));
      } finally {
        setIsRematchRequesting(false);
      }
    })();
  }, [
    clearRematchCountdownState,
    isRematchRequesting,
    localState,
    matchmaking.gameInfo?.lobbyId,
    matchmaking.gameResult?.lobbyId,
    seedRematchCountdownState,
    t,
  ]);

  const handleDeclineRematch = useCallback(() => {
    if (!rematchWaiting) return;
    if (isRematchDeclining) return;
    if (rematchWaiting.isAccepted) return;

    setIsRematchDeclining(true);

    (async () => {
      try {
        const body: DeclineRematchDto = {
          rematchRequestId: rematchWaiting.rematchRequestId,
        };

        const result = await requestApi<void>({
          method: 'post',
          url: '/api/matchmaking/rematch/decline',
          body,
          options: {
            errorInterceptor: (response: { status: number }) => {
              if (response.status === 409) return true;
              return undefined;
            },
          },
        });

        if (!result.success && result.status !== 409) {
          toast?.error(
            translateMatchmakingError(
              t,
              result.error ?? 'Failed to cancel rematch',
            ),
          );
          return;
        }

        clearRematchCountdownState(rematchWaiting.previousResultState);
      } catch (err) {
        console.error('Rematch decline failed', err);
        toast?.error(t('error.rematchCancelFailed'));
      } finally {
        setIsRematchDeclining(false);
      }
    })();
  }, [clearRematchCountdownState, isRematchDeclining, rematchWaiting, t]);

  // Render screens by state
  const renderScreen = () => {
    switch (localState) {
      case 'idle':
      case 'stake_selection':
        return (
          <StakeSelectionScreen
            externalError={matchmaking.error}
            game={game}
            gameMetadata={gameMetadata}
            initialStake={stakeSelectionInitialStake}
            insufficientBalanceModalNonce={insufficientBalanceModalNonce}
            nextLeagueWins={nextLeagueWins}
            onFindOpponent={handleFindOpponent}
            onFreePlay={handleFreePlay}
            stakeCapCents={stakeCapCents}
            userBalance={userBalance}
          />
        );

      case 'connecting':
        return <SearchingScreen game={game} message={t('search.restoring')} />;

      case 'searching':
        return (
          <SearchingScreen
            game={game}
            initialElapsedTimeSeconds={searchInitialElapsedTimeSeconds}
            isCancelling={isCancellingSearch}
            longWaitMessage={t('search.longWait')}
            longWaitThresholdSeconds={120}
            message={translateMatchmakingSearchMessage(
              t,
              matchmaking.searchState?.message,
            )}
            onCancel={handleCancelSearch}
          />
        );

      case 'waiting_for_opponent':
        return (
          <SearchingScreen
            countdownMs={confirmTimeLeftMs}
            game={game}
            message={t('search.waitingForOpponent')}
          />
        );

      case 'stake_offer':
        if (!matchmaking.stakeOffer) {
          return <SearchingScreen game={game} />;
        }
        return (
          <StakeOfferScreen
            onAccept={handleAcceptStakeOffer}
            onDecline={handleDeclineStakeOffer}
            reason={matchmaking.stakeOffer.reason}
            suggestedStake={matchmaking.stakeOffer.suggestedStake}
          />
        );

      case 'stake_offer_declined':
        return (
          <StakeOfferDeclinedScreen onContinue={handleContinueFromDeclined} />
        );

      case 'game_confirm':
        if (!matchmaking.gameInfo) {
          return <SearchingScreen game={game} />;
        }
        return (
          <GameConfirmScreen
            onAccept={handleAcceptGame}
            onDecline={handleDeclineGame}
            opponentName={matchmaking.gameInfo.opponentName}
            stake={matchmaking.gameInfo.stake}
            timeLeftMs={confirmTimeLeftMs}
          />
        );

      case 'preparing':
        return (
          <SearchingScreen game={game} message={t('search.loadingGame')} />
        );

      case 'playing':
        return (
          <GameScreen
            gameSlug={game.slug}
            gameUpdate={matchmaking.gameUpdate}
            initialPlayer1TimeLeftMs={matchmaking.gameInfo?.player1TimeLeftMs}
            initialPlayer2TimeLeftMs={matchmaking.gameInfo?.player2TimeLeftMs}
            initialPlayerSlot={matchmaking.gameInfo?.playerSlot}
            lobbyId={matchmaking.gameInfo?.lobbyId}
            onGameEnd={() => undefined}
            onMove={(action) => {
              matchmaking.sendGameAction(action as IGameAction);
            }}
            onSurrender={() => {
              matchmaking.sendGameAction({ surrender: true });
            }}
            onTimeUp={matchmaking.requestGameState}
            player1={{
              name: matchmaking.gameInfo?.creatorName || t('common.you'),
              avatarUrl: matchmaking.gameInfo?.creatorAvatar,
            }}
            player2={{
              name: matchmaking.gameInfo?.opponentName || t('common.opponent'),
              avatarUrl: matchmaking.gameInfo?.opponentAvatar,
            }}
            rules={localizedGame.rules}
          />
        );

      case 'win':
        return (
          <WinScreen
            isFreePlay={isFreePlayMatchResult}
            onFindNewOpponent={handleFindNewOpponent}
            onRematch={handleRematch}
            payout={matchmaking.gameResult?.prize ?? 0}
          />
        );

      case 'lose':
        return (
          <LoseScreen
            isFreePlay={isFreePlayMatchResult}
            onFindNewOpponent={handleFindNewOpponent}
            onRematch={handleRematch}
          />
        );

      case 'draw':
        return (
          <DrawScreen
            isFreePlay={isFreePlayMatchResult}
            onFindNewOpponent={handleFindNewOpponent}
            onRematch={handleRematch}
          />
        );

      case 'active_game_exists':
        return (
          <ActiveGameExistsScreen
            onBack={handleBackFromActiveGameExists}
            onGoToActiveGame={handleGoToActiveGame}
          />
        );

      case 'rematch_waiting':
        return (
          <RematchWaitingScreen
            game={game}
            isDeclining={isRematchDeclining}
            key={rematchWaiting?.rematchRequestId ?? 'rematch-waiting-screen'}
            onCancel={handleDeclineRematch}
            rematchRequestId={rematchWaiting?.rematchRequestId}
            timeLeftMs={rematchTimeLeftMs ?? 0}
          />
        );

      case 'cancelled':
        return (
          <CancelledScreen
            message={matchmaking.error ?? undefined}
            onFindNewOpponent={handleFindNewOpponent}
          />
        );

      case 'error':
        return <ErrorScreen error={matchmaking.error} onRetry={handleRetry} />;

      default:
        return (
          <StakeSelectionScreen
            externalError={matchmaking.error}
            game={game}
            gameMetadata={gameMetadata}
            initialStake={stakeSelectionInitialStake}
            insufficientBalanceModalNonce={insufficientBalanceModalNonce}
            nextLeagueWins={nextLeagueWins}
            onFindOpponent={handleFindOpponent}
            onFreePlay={handleFreePlay}
            stakeCapCents={stakeCapCents}
            userBalance={userBalance}
          />
        );
    }
  };

  return (
    <>
      <Modal
        classNameModal={styles.modal}
        hideCloseButton={!canClose}
        isAutoClosing={canClose}
        isBlocking
        onBeforeClose={onClose}
        preventOutsideClose
        size="fullwidth"
        title={t('modal.title')}
      >
        <div className={styles.matchmakingModal}>{renderScreen()}</div>
      </Modal>
    </>
  );
};
